import React, {Component} from 'react';
import {
    Button,
    Dialog,
    DialogTitle,
    Flex,
    Text,
    Input,
    Icon,
    PriorityIcon,
    ToggleSwitch
} from '@priority-software/priority-style-react';
import classnames from 'classnames';

import styles from '../styles/MacroDialog.module.scss';
import texts from '../texts';
import {helpPopoverRef} from '../../../_shared/Help/constants';
import {keys} from '../../../constants/KeyCodes';
import {getTopDialogZindex} from '../../../_shared/GWTBridge/invoke/priModalDlg';
import {
    getMacroSameName,
    isRecursiveByActions,
    getMacroSameKey,
    setMacroKey,
    startMacro,
    startRecording,
    updateMacroRecursive
} from '../actions/macroActions';
import {
    showConfirmMessage,
    showInfoMessage
} from '../../../_shared/Messages/actions/MessageActions';
import {returnFocus} from '../../../_shared/GWTBridge/invoke/genPage';
import {showSuccessToast} from '../../../components/Toast/actions/ToastActions';
import {setHasModalChild} from '../../../_shared/GWTBridge/invoke/netForm';
import {getServerTexts} from '../../../features/Automations/texts';

export const macroDlgRef: React.RefObject<MacroDialog> = React.createRef();

export async function renderMacroDialog(root) {
    await getServerTexts();
    root.render(<MacroDialog ref={macroDlgRef} />);
    return macroDlgRef;
}

interface MacroData {
    name?: string;
    key?: string;
    id?: string;
    isRecursive?: boolean;
    macroActions?: any[];
}

export class MacroDialog extends Component<any, any> {
    //initialize
    //#region
    state = {
        isOpen: false,
        name: '',
        key: '1',
        title: '',
        id: -1,
        isRecursive: false,
        isNew: true,
        macroActions: null,
        origKey: '',
        isDirty: false
    };
    //refs
    helpIconRef: React.RefObject<any> = React.createRef();
    saveAndExecuteRef: React.RefObject<HTMLInputElement> = React.createRef();
    //consts
    MAX_REPEAT_TIEMS = 5; //TO DO get max repeat times

    namePlaceHolder;
    //#endregion

    //handlers
    //#region

    handleShowDialog = async (macroData?: MacroData) => {
        let key = '';
        let name = '';
        let isRecursive = false;
        let isNew = true;
        let title = texts.dlg_macroSettings;
        let id = '-1';
        let macroActions;

        if (macroData) {
            name = macroData.name;
            key = macroData.key;
            title = texts.dlg_editMacroTitle.replace('XXX', name);
            isRecursive = macroData.isRecursive;
            id = macroData.id;
            isRecursive = macroData.isRecursive;
            isNew = false;
            macroActions = macroData.macroActions;
        }
        const origKey = key;

        window.addEventListener('keydown', this.handleKeyDown, false);
        this.setState({
            name,
            key,
            title,
            isOpen: true,
            id,
            isRecursive,
            isNew,
            macroActions,
            origKey,
            isDirty: false
        });

        setHasModalChild(true);
    };

    handelClose = () => {
        if (this.state.isDirty && !this.state.isNew) {
            showConfirmMessage({
                title: texts.dlg_unsavedMsgContent,
                cancleButtonText: texts.dlg_unsavedMsgNoBtn,
                confirmButtonText: texts.dlg_unsavedMsgYesBtn,
                defaultButton: 0,
                onConfirm: () => {
                    this.setState({isDirty: false}, () => {
                        this.handelClose();
                    });
                },
                onCancel: () => {
                    this.saveAndExecuteRef.current?.focus();
                }
            });
        } else {
            this.setState({isOpen: false});
            returnFocus();
            setHasModalChild(false);
            window.removeEventListener('keydown', this.handleKeyDown, false);
        }
    };

    handelShowRepeatHelpMessage = () => {
        helpPopoverRef.current.showHelpPopover({
            title: 'Help',
            content: texts.dlg_help,
            anchor: this.helpIconRef?.current
        });
    };

    onStartRecording = async () => {
        if (this.state.name) {
            this.onSaveMacro(() => this.handleStartRecording());
        }
    };

    async handleStartRecording() {
        const {name, key, isRecursive} = this.state;
        const success = await startRecording(name, key, isRecursive);
        success && this.handelClose();
    }

    handleKeyDown = e => {
        if (e.key === keys.ENTER && e.target.nodeName !== 'BUTTON') {
            e.stopPropagation();
            e.preventDefault();

            if (this.state.isNew) {
                this.onStartRecording();
            } else {
                this.state.isDirty ? this.handleSaveMacro() : this.saveAndRun();
            }
        }
    };

    saveAndRun = () => {
        const {id} = this.state;
        this.onSaveMacro(async () => {
            await this.saveMacro();
            startMacro(id);
        });
    };

    handleSaveMacro = () => {
        this.onSaveMacro(async () => {
            await this.saveMacro();
        });
    };
    saveMacro = async () => {
        const {id, isRecursive, macroActions, origKey, key} = this.state;
        if (isRecursiveByActions(macroActions, id) !== isRecursive) {
            await updateMacroRecursive(id, macroActions, isRecursive);
        }
        if (origKey !== key) {
            await setMacroKey(id, key);
        }
        const isDirty = this.state.isDirty;
        this.setState({isDirty: false}, () => {
            const text = this.state.key
                ? texts.dlg_saveSuccessMsg.replace('+X', '+' + this.state.key)
                : texts.dlg_saveSuccessNoKey;
            isDirty && showSuccessToast(text.replace('XXX', this.state.name));
            this.handelClose();
        });
    };

    onSaveMacro = (continueAction, ignoreName = false, ignoreKey = false) => {
        const {name, key, id} = this.state;

        const macroWithSameKey = getMacroSameKey(key, id);
        const macroWithSameName = getMacroSameName(name, id);

        const showSameNameMessage = !ignoreName && macroWithSameName;
        const showSameKeyMessage = !ignoreKey && macroWithSameKey;

        if (!showSameKeyMessage && !showSameNameMessage) {
            continueAction();
        }

        if (showSameNameMessage) {
            if (macroWithSameName.maydelete === '1') {
                showConfirmMessage({
                    title: texts.dlg_nameExistMsgContent,
                    onConfirm: () => this.onSaveMacro(continueAction, true, ignoreKey),
                    confirmButtonText: texts.dlg_yes,
                    cancleButtonText: texts.dlg_no,
                    defaultButton: 0
                });
            } else {
                showInfoMessage({
                    title: texts.dlg_nameExistMsgContentNoPremissions,
                    confirmButtonText: texts.dlg_accept
                });
            }
        } else if (showSameKeyMessage) {
            if (macroWithSameKey.maydelete === '1') {
                showConfirmMessage({
                    title: texts.dlg_sameKeyMessage
                        .replace('XXX', macroWithSameKey.origtitle)
                        .replace('YYY', name)
                        .replace('+X', '+' + this.state.key),
                    onConfirm: () => this.onSaveMacro(continueAction, ignoreName, true),
                    confirmButtonText: texts.dlg_yes,
                    cancleButtonText: texts.dlg_no,
                    defaultButton: 0
                });
            } else {
                showInfoMessage({
                    title: texts.dlg_sameKeyMessage2
                        .replace('XXX', macroWithSameKey.origtitle)
                        .replace('YYY', name)
                        .replace('+X', '+' + this.state.key),
                    confirmButtonText: texts.dlg_accept
                });
            }
        }
    };

    //#endregion

    //UI
    //#region

    renderContent = () => (
        <>
            <Flex justifyContent="flex-start">
                {this.state.isNew && (
                    <Input
                        autoSelect
                        autoFocus
                        size="large"
                        componentClasses={classnames(
                            styles.input,
                            styles.nameInput,
                            styles.marginItemBig
                        )}
                        label={
                            <Text componentClasses={styles.inputLabel} skin="subtitle-1">
                                {texts.dlg_inputNameLabel}
                            </Text>
                        }
                        value={this.state.name}
                        onChange={event => {
                            const value = event.target?.value;
                            if (value?.length <= 40) {
                                this.setState({name: event.target.value, isDirty: true});
                            }
                        }}
                    />
                )}

                <Flex direction="column" alignItems="flex-start">
                    <Text
                        componentClasses={styles.inputLabel}
                        skin="subtitle-1"
                        textColor="primary-700"
                    >
                        {texts.dlg_inputKeyLabel}
                    </Text>
                    <Flex justifyContent="flex-start">
                        <Text componentClasses={styles.marginItemSmall} skin="paragraph-2">
                            ALT +
                        </Text>

                        <Input
                            componentClasses={classnames(styles.input, styles.keyInput)}
                            size="large"
                            type="number"
                            value={this.state.key}
                            autoFocus={!this.state.isNew}
                            onChange={event => {
                                const value = event.target.value;
                                if (value.length < 2 && (Number(value) >= 0 || !value)) {
                                    this.setState({key: value, isDirty: true});
                                }
                            }}
                        />
                    </Flex>
                </Flex>
            </Flex>
            <Flex componentClasses={styles.repeatTitle} justifyContent="flex-start">
                <Text skin="subtitle-1" textColor="primary-700">
                    {texts.dlg_repeatTitle}
                </Text>
                <div ref={this.helpIconRef}>
                    <Icon
                        componentClasses={styles.helpIcon}
                        onClick={this.handelShowRepeatHelpMessage}
                        icon={PriorityIcon.help}
                    />
                </div>
            </Flex>
            <Flex justifyContent="flex-start">
                <ToggleSwitch
                    onChange={() =>
                        this.setState(prevState => ({
                            isRecursive: !prevState.isRecursive,
                            isDirty: true
                        }))
                    }
                    checked={this.state.isRecursive}
                />
            </Flex>
        </>
    );

    renderFooter = () => {
        const {isNew, isDirty, name, key, origKey} = this.state;
        const disableSave = key === '' && origKey !== key;
        return isNew ? (
            <Flex justifyContent="flex-start">
                <Button
                    componentClasses={styles.marginItemSmall}
                    width="regularFixed"
                    skin="primary"
                    size="large"
                    disabled={name === ''}
                    onClick={this.onStartRecording}
                >
                    {texts.dlg_footerSave}
                </Button>
            </Flex>
        ) : (
            <Flex justifyContent="flex-start">
                <Button
                    componentClasses={styles.marginItemSmall}
                    width="regularFixed"
                    skin="primary"
                    size="large"
                    autoFocus
                    disabled={disableSave || !isDirty}
                    onClick={this.handleSaveMacro}
                >
                    {texts.dlg_saveBtn}
                </Button>
                <Button
                    componentClasses={styles.marginItemSmall}
                    width="regularFixed"
                    skin="secondary"
                    size="large"
                    disabled={disableSave}
                    onClick={this.saveAndRun}
                    inputRef={this.saveAndExecuteRef}
                >
                    {texts.dlg_footerSaveExecute}
                </Button>
            </Flex>
        );
    };
    //#endregion

    render() {
        return (
            this.state.isOpen && (
                <Dialog
                    draggable
                    size="large"
                    isOpen={this.state.isOpen}
                    onClose={() => this.handelClose()}
                    title={<DialogTitle skin="h2" title={this.state.title} />}
                    content={this.renderContent()}
                    footer={this.renderFooter()}
                    zIndex={getTopDialogZindex()}
                />
            )
        );
    }
}
